import AppUser from "@toothfairy/shared-ui/AppUser";
import { useEffect, useState } from "react";
import { Platform } from "react-native";
import { isElectronRunning } from "../../App";
import useUserHelperPreferences from "./useUserHelperPreferences";

export const TOUR_FLAGS = {
  workspaceTourCompleted: "workspaceTourCompleted",
  knowledgeHubTourCompleted: "knowledgeHubTourCompleted",
  knowledgeHubVisited: "knowledgeHubVisited",
  chatsTourCompleted: "chatsTourCompleted",
  settingsIntrosSeen: "settingsIntrosSeen",
  onboardingCompleted: "onboardingCompleted",
};

const ALL_RESETTABLE_FLAGS = [
  TOUR_FLAGS.workspaceTourCompleted,
  TOUR_FLAGS.knowledgeHubTourCompleted,
  TOUR_FLAGS.knowledgeHubVisited,
  TOUR_FLAGS.chatsTourCompleted,
  TOUR_FLAGS.settingsIntrosSeen,
  TOUR_FLAGS.onboardingCompleted,
];

const ALL_REQUIRED_TOURS = [
  TOUR_FLAGS.workspaceTourCompleted,
  TOUR_FLAGS.knowledgeHubTourCompleted,
  TOUR_FLAGS.chatsTourCompleted,
];

export const SETTINGS_INTROS_SEEN_KEY = TOUR_FLAGS.settingsIntrosSeen;

const parsePreferences = (raw) => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }
  return raw;
};

const useOnboardingTour = () => {
  const { user } = AppUser.useUser();
  const { updateUserHelperPreferences } = useUserHelperPreferences();

  const isPlatformSupported = Platform.OS === "web" || isElectronRunning();

  const getPreferences = () => parsePreferences(user?.userHelperPreferences);

  const needsTour = (key) => {
    if (!isPlatformSupported) return false;
    const prefs = getPreferences();
    return prefs[key] !== true;
  };

  const completeTour = (key) => {
    if (!isPlatformSupported || !key) return;
    updateUserHelperPreferences(user, { [key]: true });
  };

  const completeTourIfEligible = (key) => {
    if (!isPlatformSupported || !key) return;
    const prefs = getPreferences();
    if (prefs[key] === true) return;
    const patch = { [key]: true };
    const allDone = ALL_REQUIRED_TOURS.every((k) =>
      k === key ? true : prefs[k] === true,
    );
    if (allDone) patch[TOUR_FLAGS.onboardingCompleted] = true;
    updateUserHelperPreferences(user, patch);
  };

  const resetOnboarding = () => {
    if (!isPlatformSupported) return;
    const cleared = ALL_RESETTABLE_FLAGS.reduce((acc, k) => {
      acc[k] = false;
      return acc;
    }, {});
    updateUserHelperPreferences(user, cleared);
  };

  const getSeenSectionIntros = () => {
    const prefs = getPreferences();
    const seen = prefs[SETTINGS_INTROS_SEEN_KEY];
    return seen && typeof seen === "object" ? seen : {};
  };

  const needsSectionIntro = (section) => {
    if (!isPlatformSupported || !section) return false;
    const seen = getSeenSectionIntros();
    return seen[section] !== true;
  };

  const markSectionIntroSeen = (section) => {
    if (!isPlatformSupported || !section) return;
    const seen = getSeenSectionIntros();
    if (seen[section] === true) return;
    const next = { ...seen, [section]: true };
    updateUserHelperPreferences(user, { [SETTINGS_INTROS_SEEN_KEY]: next });
  };

  const hasCompletedAllTours = () => {
    const prefs = getPreferences();
    return ALL_REQUIRED_TOURS.every((k) => prefs[k] === true);
  };

  const isOnboardingComplete = () => {
    const prefs = getPreferences();
    return prefs[TOUR_FLAGS.onboardingCompleted] === true;
  };

  const useTourReady = (selectors = []) => {
    const [ready, setReady] = useState(false);
    useEffect(() => {
      if (!isPlatformSupported) return;
      setReady(false);
      if (!selectors?.length) {
        setReady(true);
        return;
      }
      let cancelled = false;
      const startedAt = Date.now();
      const anyMissing = () =>
        selectors.some(
          (sel) =>
            typeof document !== "undefined" && !document.querySelector(sel),
        );
      const interval = setInterval(() => {
        if (cancelled) return;
        if (!anyMissing()) {
          setReady(true);
          clearInterval(interval);
        } else if (Date.now() - startedAt > 2500) {
          setReady(true);
          clearInterval(interval);
        }
      }, 100);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }, [JSON.stringify(selectors)]);
    return ready;
  };

  return {
    isPlatformSupported,
    needsTour,
    completeTour,
    completeTourIfEligible,
    resetOnboarding,
    needsSectionIntro,
    markSectionIntroSeen,
    hasCompletedAllTours,
    isOnboardingComplete,
    useTourReady,
  };
};

export default useOnboardingTour;
